/**
 * Section Product List Page (View More)
 * Displays products for a home section (Top Deals, New Arrivals, Best Selling, Recommended)
 * with banner title/description from the View More button and horizontal category filters.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  useParams,
  useNavigate,
  useLocation } from "react-router-dom";
import {
  EnhancedProductCard,
  Button
} from "@b2b/ui-components";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { homepageService, sellerService } from "@b2b/services";
import { mapProductToCardFormat } from "../../utils/productMapper";
import { useAuth } from "../../auth/Authprovider";
import { useChatUnreadHeader } from "../../chat/ChatUnreadHeaderProvider";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../../components/marketing/BizcoverIndiaFigmaFooter";

/** Banner bg from navigation state, API section, or default. */
const FALLBACK_BANNER_BG = "#F97316";

const PAGE_SIZE = 20;
const FETCH_LIMIT = 100;

/** Category node from hierarchy API (id, name, parentId, children). */
interface CategoryNode {
  id: string;
  name: string;
  slug?: string;
  parentId?: string | null;
  children?: CategoryNode[];
}

/** Product category label — keep in sync with BizcoverIndiaUpdatedHomePage mapHomepageProductToCardItem. */
function getCategoryName(product: any): string | null {
  const raw =
    product?.categoryRelation?.name ??
    (typeof product?.category === "string" ? product.category : product?.category?.name) ??
    product?.categoryName ??
    product?.subcategory ??
    null;
  if (raw == null) return null;
  const s = typeof raw === "string" ? raw : String(raw).trim();
  return s.trim() || null;
}

/** Top-level (parent) categories only. */
function getParentCategories(hierarchy: CategoryNode[]): CategoryNode[] {
  return hierarchy.filter((c) => c.parentId == null || c.parentId === "");
}

/** All names for a node and its descendants (for parent filter). */
function getDescendantNames(node: CategoryNode): Set<string> {
  const names = new Set<string>();
  names.add(node.name);
  if (node.children?.length) {
    node.children.forEach((c) => getDescendantNames(c).forEach((n) => names.add(n)));
  }
  return names;
}

const SectionProductListPage: React.FC = () => {
  const { sectionKey } = useParams<{ sectionKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userRole } = useAuth();
  const chatUnread = useChatUnreadHeader();

  const state = (location.state as {
    sectionTitle?: string;
    sectionDescription?: string;
    bannerBgColor?: string;
  }) || {};
  const [apiSection, setApiSection] = useState<{
    title: string;
    description: string | null;
    bannerAccentColor: string | null;
  } | null>(null);

  const sectionTitle = state.sectionTitle ?? apiSection?.title ?? "Products";
  const sectionDescription = state.sectionDescription ?? apiSection?.description ?? "";
  const sectionBannerBgColor =
    state.bannerBgColor ?? apiSection?.bannerAccentColor ?? FALLBACK_BANNER_BG;
  const isCssColor = (v: string) => /^#|^rgb\(|^rgba\(|^hsl\(|^hsla\(/.test((v || "").trim());
  const bannerBgResolved = isCssColor(sectionBannerBgColor) ? sectionBannerBgColor.trim() : FALLBACK_BANNER_BG;

  const [categoryHierarchy, setCategoryHierarchy] = useState<CategoryNode[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<string>("all");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>("all");
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sectionNotFound, setSectionNotFound] = useState(false);
  const [page, setPage] = useState(1);
  const parentRowRef = useRef<HTMLDivElement | null>(null);
  const parentButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Fetch category hierarchy for parent/subcategory filters
  useEffect(() => {
    sellerService
      .getCategories(true)
      .then((data: any) => {
        const list = Array.isArray(data) ? data : [];
        setCategoryHierarchy(list);
      })
      .catch((err) => console.error("Error fetching categories:", err));
  }, []);

  const parentOptions = useMemo(() => {
    const parents = getParentCategories(categoryHierarchy);
    const withAll = [{ id: "all", name: "All" }, ...parents.map((p) => ({ id: p.id, name: p.name || p.slug || p.id }))];
    return withAll;
  }, [categoryHierarchy]);

  const selectedParentNode = useMemo(() => {
    if (selectedParentId === "all") return null;
    return getParentCategories(categoryHierarchy).find((p) => p.id === selectedParentId) ?? null;
  }, [categoryHierarchy, selectedParentId]);

  const subcategoryOptions = useMemo(() => {
    const productCategoryNames = new Set<string>();
    allProducts.forEach((p) => {
      const n = getCategoryName(p);
      if (n) productCategoryNames.add(n);
    });
    if (selectedParentId === "all") {
      // Labels actually present on these products (may be parent names only).
      // Intersecting only with hierarchy "non-root" names hides every chip when API uses parent labels.
      const sorted = [...productCategoryNames].sort((a, b) => a.localeCompare(b));
      return [{ id: "all", name: "All" }, ...sorted.map((name) => ({ id: name, name }))];
    }
    if (!selectedParentNode) return [{ id: "all", name: "All" }];
    // Include selected parent name — products may be tagged with the parent, not a leaf.
    const allowedInTree = getDescendantNames(selectedParentNode);
    const matched = [...productCategoryNames].filter((name) => allowedInTree.has(name));
    const sorted = [...new Set(matched)].sort((a, b) => a.localeCompare(b));
    return [{ id: "all", name: "All" }, ...sorted.map((name) => ({ id: name, name }))];
  }, [categoryHierarchy, selectedParentId, selectedParentNode, allProducts]);

  useEffect(() => {
    if (!sectionKey) {
      setLoading(false);
      setSectionNotFound(true);
      return;
    }

    const slug = decodeURIComponent(sectionKey);
    let active = true;
    setLoading(true);
    setError(null);
    setSectionNotFound(false);

    homepageService
      .getSectionProductsBySlug(slug)
      .then((res) => {
        if (!active) return;
        setApiSection(res.section);
        const products = res.products;
        setAllProducts(Array.isArray(products) ? products : []);
        setSelectedParentId("all");
        setSelectedSubcategoryId("all");
        setPage(1);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        console.error("Error fetching section products:", err);
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setSectionNotFound(true);
        } else {
          setError((err as Error)?.message ?? "Failed to load products");
        }
        setAllProducts([]);
        setApiSection(null);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [sectionKey]);

  const filteredProducts = useMemo(() => {
    const nameMatch = (p: any, targetName: string) => {
      const n = getCategoryName(p);
      return n != null && n.toLowerCase() === targetName.toLowerCase();
    };
    if (selectedParentId === "all" && selectedSubcategoryId === "all") return allProducts;
    if (selectedParentId === "all") {
      return allProducts.filter((p) => nameMatch(p, selectedSubcategoryId));
    }
    const parentNames = selectedParentNode ? getDescendantNames(selectedParentNode) : new Set<string>();
    const parentNamesLower = new Set([...parentNames].map((s) => s.toLowerCase()));
    if (selectedSubcategoryId === "all") {
      return allProducts.filter((p) => {
        const n = getCategoryName(p);
        return n != null && (parentNames.has(n) || parentNamesLower.has(n.toLowerCase()));
      });
    }
    return allProducts.filter((p) => nameMatch(p, selectedSubcategoryId));
  }, [allProducts, selectedParentId, selectedSubcategoryId, selectedParentNode]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const paginatedProducts = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, page]);

  const cardProducts = useMemo(
    () =>
      paginatedProducts.map((p) => {
        const card = mapProductToCardFormat(p);
        return {
          ...card,
          supplier: {
            ...card.supplier,
            rating: card.supplier?.rating ?? 4,
          },
        };
      }),
    [paginatedProducts]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollCategoryRow = (rowId: string, dir: "left" | "right") => {
    const el = document.getElementById(rowId);
    if (el) el.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
  };

  const scrollParentCategoryIntoView = (categoryId: string) => {
    const button = parentButtonRefs.current[categoryId];
    if (!button) return;
    try {
      button.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    } catch {
      button.scrollIntoView(true);
    }
  };

  if (!sectionKey || sectionNotFound) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">
          Section not found.{" "}
          <button type="button" className="text-[#F97316] underline" onClick={() => navigate("/")}>
            Go to Home
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <style>{`
        .header-no-padding nav > div.max-w-7xl,
        .header-no-padding nav > div > div.max-w-7xl {
          padding-left: 40px !important;
          padding-right: 50px !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          max-width: 100% !important;
        }
      `}</style>
      <div className="header-no-padding">
        <BizcoverIndiaFigmaHeader />
      </div>

      {/* Banner - background color from state.bannerBgColor or section config (use hex/rgb/hsl, e.g. #F97316) */}
      <section
        className="w-full text-white py-8 px-4 sm:px-6 lg:px-10"
        style={{ backgroundColor: bannerBgResolved }}
      >
        <div className="max-w-[1440px] mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 text-center">{sectionTitle}</h1>
          {sectionDescription && (
            <p className="text-base sm:text-lg text-white/95 text-center">{sectionDescription}</p>
          )}
        </div>
      </section>

      {/* Row 1: Parent categories (single-line horizontal scroll) */}
      <section className="border-b border-gray-200 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-10 py-3">
          <div className="relative flex items-center">
            <button
              type="button"
              onClick={() => scrollCategoryRow("section-parent-scroll", "left")}
              className="flex-shrink-0 p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Scroll parent categories left"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div
              id="section-parent-scroll"
              ref={parentRowRef}
              className="flex-1 flex gap-2 overflow-x-auto scrollbar-hide scroll-smooth py-1 px-1"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {parentOptions.map((cat) => (
                <button
                  key={cat.id}
                  ref={(el) => {
                    if (el) {
                      parentButtonRefs.current[cat.id] = el;
                    }
                  }}
                  type="button"
                  onClick={() => {
                    setSelectedParentId(cat.id);
                    setSelectedSubcategoryId("all");
                    setPage(1);
                    scrollParentCategoryIntoView(cat.id);
                  }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                    selectedParentId === cat.id
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => scrollCategoryRow("section-parent-scroll", "right")}
              className="flex-shrink-0 p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Scroll parent categories right"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </section>

      {/* Row 2: Subcategories (no scroll - inline/wrap) */}
      <section className=" border-gray-200 bg-white">
        <div className="w-full px-2 sm:px-6 lg:px-10 py-3">
          <div className="flex flex-wrap gap-2">
            {subcategoryOptions.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  setSelectedSubcategoryId(cat.id);
                  setPage(1);
                }}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  selectedSubcategoryId === cat.id
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product grid */}
      <div className="flex-1 bg-gray-50">
        <div className="w-full px-2 sm:px-6 lg:px-10 py-4">
          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="h-[320px] bg-gray-200 animate-pulse rounded-lg"
                />
              ))}
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-16">
              <p className="text-red-600 mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Try again
              </Button>
            </div>
          )}

          {!loading && !error && cardProducts.length === 0 && (
            <div className="text-center py-16 text-gray-600">
              No products found
              {selectedParentId !== "all" || selectedSubcategoryId !== "all"
                ? " for the selected category."
                : "."}
            </div>
          )}

          {!loading && !error && cardProducts.length > 0 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {cardProducts.map((product) => (
                  <div key={product.id}>
                    <EnhancedProductCard
                      product={{
                        id: product.id,
                        title: product.title,
                        price: product.price,
                        moq: product.moq,
                        score: product.score ?? 4.5,
                        supplier: product.supplier,
                        image: product.image,
                      }}
                    />
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600 px-2">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <BizcoverIndiaFigmaFooter />

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default SectionProductListPage;
